import React, { useState, useEffect } from 'react';
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,

  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Card, 
  CardContent,
  InputAdornment,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import { 
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Undo as UndoIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { 
  startOfMonth, 
  endOfMonth, 
  format, 
  isToday, 
  isSameDay,
  parseISO,
  getYear,
  getMonth,
  startOfDay,
  endOfDay
} from 'date-fns';
import { getCommissions, Commission } from '../firebase/financeServices';
import { subscribeToCommissions } from '../services/commissionService';
import { validateCommissions, subscribeToCommissionValidation, CommissionValidationIssue } from '../services/validationService';
import { 
  generateCommissionGLEntryPreviews, 
  createMonthlyCommissionGLTransaction, 
  checkMonthlyCommissionGLTransaction,
  reverseMonthlyCommissionGLTransaction,
  getMonthlyCommissionGLTransactionForReversal,
  GLEntryPreview,
  MonthlyCommissionData,
  fetchMonthlyCommissionData
} from '../services/glCommissionService';
import { GLTransaction, GLTransactionEntry } from '../firebase/glTransactionServices';
import CommissionGLEntryPreviewDialog from '../components/CommissionGLEntryPreviewDialog';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

// Helper function to format currency with RM
const formatCurrency = (amount: number): string => {
  return `RM ${amount.toFixed(2)}`;
};

// Helper function to format currency for display in cards (larger font)
const formatCurrencyDisplay = (amount: number): { value: string, rmPart: string, amountPart: string } => {
  const amountStr = amount.toFixed(2);
  
  return {
    value: `RM ${amountStr}`,
    rmPart: 'RM',
    amountPart: amountStr
  };
};

// Helper function to format date
const formatDate = (date: Date | string | Timestamp): string => {
  if (!date) return 'N/A';
  
  let dateObj: Date;
  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    // Handle Firestore Timestamp
    dateObj = date.toDate();
  }
  
  return format(dateObj, 'dd MMM yyyy');
};

// Helper function to determine if a commission is for a service or product
const determineCategory = async (serviceName: string, serviceId?: string): Promise<string> => {
  if (!serviceId) return 'Undefined';
  
  try {
    // First check if the serviceId exists in the services collection
    const serviceDoc = doc(db, 'services', serviceId);
    const serviceSnapshot = await getDoc(serviceDoc);
    
    if (serviceSnapshot.exists()) {
      return 'Service';
    }
    
    // If not found in services, check the products collection
    const productDoc = doc(db, 'products', serviceId);
    const productSnapshot = await getDoc(productDoc);
    
    if (productSnapshot.exists()) {
      return 'Product';
    }
    
    // If not found in either collection, return Undefined
    return 'Undefined';
  } catch (error) {
    console.error('Error determining category:', error);
    return 'Undefined';
  }
};

// No need for a synchronous fallback version anymore as we're using the async function with state

interface StaffSummary {
  staffId: string;
  staffName: string;
  totalCommission: number;
  serviceCount: number;
  commissions: Commission[];
  pendingAmount: number;
  paidAmount: number;
  voidedAmount: number;
}

const CommissionBreakdown: React.FC = () => {
  const currentYear = getYear(new Date());
  const currentMonth = getMonth(new Date());
  
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationIssues, setValidationIssues] = useState<CommissionValidationIssue[]>([]);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  
  // GL entry generation states
  const [commissionGlEntryPreviewOpen, setCommissionGlEntryPreviewOpen] = useState<boolean>(false);
  const [commissionGlEntryPreviews, setCommissionGlEntryPreviews] = useState<GLEntryPreview[]>([]);
  const [isSubmittingCommissionGlEntries, setIsSubmittingCommissionGlEntries] = useState<boolean>(false);
  const [hasGLEntries, setHasGLEntries] = useState<boolean>(false);
  const [glTransactionId, setGlTransactionId] = useState<string | null>(null);
  const [isReversingGLEntries, setIsReversingGLEntries] = useState<boolean>(false);
  const [reverseDialogOpen, setReverseDialogOpen] = useState<boolean>(false);
  const [glTransactionToReverse, setGlTransactionToReverse] = useState<GLTransaction | null>(null);
  const [loadingTransactionDetails, setLoadingTransactionDetails] = useState<boolean>(false);
  const [serviceCommission, setServiceCommission] = useState<number>(0);
  const [productCommission, setProductCommission] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [filteredCommissions, setFilteredCommissions] = useState<Commission[]>([]);
  const [commissionCategories, setCommissionCategories] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [staffSummaries, setStaffSummaries] = useState<StaffSummary[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<string>('all');
  
  const { enqueueSnackbar } = useSnackbar();
  
  // Generate years for dropdown (5 years back, 2 years forward)
  const years = Array.from({ length: 8 }, (_, i) => currentYear - 5 + i);
  
  // Month names for dropdown
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Status display mapping
  const statusDisplayMap: Record<string, { label: string, color: 'success' | 'warning' | 'error' | 'default' }> = {
    'paid': { label: 'Paid', color: 'success' },
    'pending': { label: 'Pending', color: 'warning' },
    'voided': { label: 'Voided', color: 'error' }
  };

  // Check if GL entries already exist for the selected month
  const checkExistingGLEntries = async () => {
    try {
      const transactionId = await checkMonthlyCommissionGLTransaction(selectedYear, selectedMonth);
      console.log('Existing GL transaction ID:', transactionId);
      setHasGLEntries(!!transactionId);
      setGlTransactionId(transactionId);
    } catch (error) {
      console.error('Error checking existing GL entries:', error);
    }
  };

  useEffect(() => {
    fetchCommissionData(selectedYear, selectedMonth);
    checkExistingGLEntries();
    
    // Set up real-time commission updates
    const staffId = selectedStaff !== 'all' ? selectedStaff : undefined;
    const commissionUnsubscribe = subscribeToCommissions(
      selectedYear,
      selectedMonth,
      staffId,
      (commissionData) => {
        setCommissions(commissionData);
        generateStaffSummaries(commissionData);
        
        // Fetch categories for all commissions
        fetchCategoriesForCommissions(commissionData);
      }
    );
    
    // Set up real-time commission validation updates
    const validationUnsubscribe = subscribeToCommissionValidation(
      selectedYear,
      selectedMonth,
      staffId,
      (issues) => {
        setValidationIssues(issues);
      }
    );
    
    // Clean up subscriptions when component unmounts or dependencies change
    return () => {
      commissionUnsubscribe();
      validationUnsubscribe();
    };
  }, [selectedYear, selectedMonth, selectedStaff]);
  
  // Re-check GL entries when the component mounts and whenever the year/month changes
  useEffect(() => {
    checkExistingGLEntries();
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    filterCommissionData();
  }, [commissions, searchTerm, statusFilter, selectedStaff]);
  
  // Function to fetch categories for all commissions
  const fetchCategoriesForCommissions = async (commissionData: Commission[]) => {
    const categoryPromises = commissionData.map(async (commission) => {
      if (!commission.id) return null;
      
      const category = await determineCategory(commission.serviceName, commission.serviceId);
      return { id: commission.id, category };
    });
    
    const results = await Promise.all(categoryPromises);
    const newCategories: Record<string, string> = {};
    
    results.forEach(result => {
      if (result && result.id) {
        newCategories[result.id] = result.category;
      }
    });
    
    setCommissionCategories(newCategories);
  };

  const fetchCommissionData = async (year: number, month: number) => {
    setLoading(true);
    setError(null);
    
    try {
      // Create date range for the selected month
      const startDate = startOfMonth(new Date(year, month));
      const endDate = endOfMonth(new Date(year, month));
      
      // Get commissions from Firebase
      const staffId = selectedStaff !== 'all' ? selectedStaff : undefined;
      const commissionsData = await getCommissions(staffId, startDate, endDate);
      
      // Filter to only include commission splits (type='commission' or undefined for backward compatibility)
      const commissionData = commissionsData.filter(comm => comm.type === 'commission' || !comm.type);
      
      setCommissions(commissionData);
      
      // Generate staff summaries
      generateStaffSummaries(commissionData);
    } catch (err) {
      console.error('Error fetching commission data:', err);
      setError('Failed to load commission data. Please try again.');
      enqueueSnackbar('Failed to load commission data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const generateStaffSummaries = (commissionData: Commission[]) => {
    // Group commissions by staff
    const staffMap = new Map<string, Commission[]>();
    
    commissionData.forEach(commission => {
      if (!staffMap.has(commission.staffId)) {
        staffMap.set(commission.staffId, []);
      }
      staffMap.get(commission.staffId)?.push(commission);
    });
    
    // Create staff summaries
    const summaries: StaffSummary[] = [];
    
    staffMap.forEach((staffCommissions, staffId) => {
      if (staffCommissions.length > 0) {
        const staffName = staffCommissions[0].staffName;
        const totalCommission = staffCommissions.reduce((sum, comm) => sum + comm.amount, 0);
        const serviceCount = staffCommissions.length;
        
        // Calculate amounts by status
        const pendingAmount = staffCommissions
          .filter(comm => comm.status === 'pending')
          .reduce((sum, comm) => sum + comm.amount, 0);
          
        const paidAmount = staffCommissions
          .filter(comm => comm.status === 'paid')
          .reduce((sum, comm) => sum + comm.amount, 0);
          
        const voidedAmount = staffCommissions
          .filter(comm => comm.status === 'voided')
          .reduce((sum, comm) => sum + comm.amount, 0);
        
        summaries.push({
          staffId,
          staffName,
          totalCommission,
          serviceCount,
          commissions: staffCommissions,
          pendingAmount,
          paidAmount,
          voidedAmount
        });
      }
    });
    
    // Sort by total commission amount (highest first)
    summaries.sort((a, b) => b.totalCommission - a.totalCommission);
    
    setStaffSummaries(summaries);
  };

  const filterCommissionData = () => {
    let filtered = [...commissions];
    
    // Apply search filter
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(comm => 
        comm.staffName.toLowerCase().includes(term) ||
        comm.serviceName.toLowerCase().includes(term) ||
        comm.saleNumber.toLowerCase().includes(term) ||
        (comm.customerName && comm.customerName.toLowerCase().includes(term))
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(comm => comm.status === statusFilter);
    }
    
    // Apply staff filter
    if (selectedStaff !== 'all') {
      filtered = filtered.filter(comm => comm.staffId === selectedStaff);
    }
    
    setFilteredCommissions(filtered);
    setPage(0); // Reset to first page when filters change
  };
  
  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setSelectedYear(Number(event.target.value));
  };
  
  const handleMonthChange = (event: SelectChangeEvent<number>) => {
    setSelectedMonth(Number(event.target.value));
  };
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  
  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
  };
  
  const handleStaffFilterChange = (event: SelectChangeEvent<string>) => {
    setSelectedStaff(event.target.value);
  };
  
  const handleRefresh = () => {
    fetchCommissionData(selectedYear, selectedMonth);
  };
  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handlePrint = () => {
    window.print();
  };
  
  const handleValidateCommissions = async () => {
    setValidating(true);
    
    try {
      // Get staff ID if filtering by staff
      const staffId = selectedStaff !== 'all' ? selectedStaff : undefined;
      
      // Validate commissions
      const issues = await validateCommissions(selectedYear, selectedMonth, staffId);
      setValidationIssues(issues);
      setValidationDialogOpen(true);
      
      if (issues.length === 0) {
        enqueueSnackbar('All commissions are valid!', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error validating commissions:', error);
      enqueueSnackbar('Failed to validate commissions', { variant: 'error' });
    } finally {
      setValidating(false);
    }
  };
  
  // Handle opening GL entry preview dialog for commissions
  const handleGenerateCommissionGLEntries = async () => {
    try {
      console.log('Generate Commission GL Entries clicked');
      setLoading(true);
      
      // Fetch actual commission data from the commission system
      const commissionData = await fetchMonthlyCommissionData(selectedYear, selectedMonth);
      
      // Update state with the fetched commission amounts
      setServiceCommission(commissionData.serviceCommission);
      setProductCommission(commissionData.productCommission);
      
      // Generate GL entry previews
      const entries = generateCommissionGLEntryPreviews(commissionData);
      console.log('Generated entries:', entries);
      
      setCommissionGlEntryPreviews(entries);
      setCommissionGlEntryPreviewOpen(true);
    } catch (error) {
      console.error('Error generating commission GL entries:', error);
      enqueueSnackbar(`Failed to generate commission GL entries: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle confirming and posting GL entries for commissions
  const handleConfirmCommissionGLEntries = async () => {
    setIsSubmittingCommissionGlEntries(true);
    
    try {
      // Check if GL entries already exist for this month
      const existingTransactionId = await checkMonthlyCommissionGLTransaction(selectedYear, selectedMonth);
      
      if (existingTransactionId) {
        enqueueSnackbar('GL entries already exist for this month', { variant: 'warning' });
        setCommissionGlEntryPreviewOpen(false);
        return;
      }
      
      // Fetch actual commission data again to ensure it's up-to-date
      const commissionData = await fetchMonthlyCommissionData(selectedYear, selectedMonth);
      
      // Create GL transaction using the previewed entries
      const transactionId = await createMonthlyCommissionGLTransaction(commissionData, commissionGlEntryPreviews);
      
      // Update state to reflect the new GL transaction
      setHasGLEntries(true);
      setGlTransactionId(transactionId);
      
      // Close dialog and show success message
      setCommissionGlEntryPreviewOpen(false);
      enqueueSnackbar('Commission GL entries posted successfully', { variant: 'success' });
      
    } catch (error) {
      console.error('Error posting commission GL entries:', error);
      enqueueSnackbar(`Failed to post commission GL entries: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
    } finally {
      setIsSubmittingCommissionGlEntries(false);
    }
  };
  
  // Handle opening the reverse GL entries dialog
  const handleOpenReverseDialog = async () => {
    if (!glTransactionId) {
      enqueueSnackbar('No GL entries to reverse', { variant: 'error' });
      return;
    }
    
    setLoadingTransactionDetails(true);
    
    try {
      // Get the transaction details for preview
      const transaction = await getMonthlyCommissionGLTransactionForReversal(selectedYear, selectedMonth);
      
      if (!transaction) {
        enqueueSnackbar('Could not find GL transaction details', { variant: 'error' });
        return;
      }
      
      setGlTransactionToReverse(transaction);
      setReverseDialogOpen(true);
    } catch (error) {
      console.error('Error getting transaction details for reversal:', error);
      enqueueSnackbar(`Error getting transaction details: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
    } finally {
      setLoadingTransactionDetails(false);
    }
  };
  
  // Handle reversing GL entries
  const handleReverseGLEntries = async () => {
    setIsReversingGLEntries(true);
    
    try {
      console.log('Reversing GL entries for:', selectedYear, selectedMonth);
      console.log('Transaction ID to reverse:', glTransactionId);
      
      await reverseMonthlyCommissionGLTransaction(selectedYear, selectedMonth);
      
      // Update state to reflect the reversed GL transaction
      setHasGLEntries(false);
      setGlTransactionId(null);
      
      // Close dialog and show success message
      setReverseDialogOpen(false);
      enqueueSnackbar('Commission GL entries reversed successfully', { variant: 'success' });
      
      // Refresh the commission data
      fetchCommissionData(selectedYear, selectedMonth);
      checkExistingGLEntries();
      
    } catch (error) {
      console.error('Error reversing commission GL entries:', error);
      enqueueSnackbar(`Failed to reverse commission GL entries: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
    } finally {
      setIsReversingGLEntries(false);
    }
  };
  
  const handleCloseValidationDialog = () => {
    setValidationDialogOpen(false);
  };
  
  // Calculate totals (excluding voided amounts)
  const totalCommissionAmount = filteredCommissions
    .filter(comm => comm.status !== 'voided')
    .reduce((sum, comm) => sum + comm.amount, 0);
  const totalPendingAmount = filteredCommissions
    .filter(comm => comm.status === 'pending')
    .reduce((sum, comm) => sum + comm.amount, 0);
  const totalPaidAmount = filteredCommissions
    .filter(comm => comm.status === 'paid')
    .reduce((sum, comm) => sum + comm.amount, 0);
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Month/Year Selection */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: 2,
        mb: 3 
      }}>
        <Typography variant="h5" component="h1">
          Commission Breakdown
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: 1,
          width: { xs: '100%', sm: 'auto' }
        }}>
          <FormControl size="small" sx={{ minWidth: { xs: 110, sm: 120 } }}>
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select
              labelId="month-select-label"
              id="month-select"
              value={selectedMonth}
              label="Month"
              onChange={handleMonthChange}
            >
              {months.map((month, index) => (
                <MenuItem key={month} value={index}>{month}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: { xs: 90, sm: 100 } }}>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              label="Year"
              onChange={handleYearChange}
            >
              {years.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button 
            variant="outlined" 
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          {/* Validate Commission button moved below with badge */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
            {/* GL Entry Status Indicator */}
            <Chip 
              icon={hasGLEntries ? <CheckCircleIcon /> : <ErrorIcon />}
              label={hasGLEntries ? 'GL Posted' : 'GL Not Posted'}
              color={hasGLEntries ? 'success' : 'default'}
              variant="outlined"
            />
            
            {/* Generate GL Entries Button */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateCommissionGLEntries}
              startIcon={<AccountBalanceIcon />}
              disabled={loading || commissions.length === 0 || hasGLEntries}
            >
              {loading ? 'Loading...' : (hasGLEntries ? 'GL Entries Generated' : 'Generate GL Entries')}
            </Button>
            
            {hasGLEntries && (
              <Tooltip title="Reverse GL Entries">
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<UndoIcon />}
                  onClick={handleOpenReverseDialog}
                  disabled={loading}
                  sx={{ ml: 1 }}
                >
                  REVERSE
                </Button>
              </Tooltip>
            )}
            
            <Button 
              variant="outlined" 
              color="primary"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ ml: 1 }}
            >
              Print
            </Button>
            <Button 
              variant="outlined" 
              color="secondary"
              size="small"
              startIcon={validating ? <CircularProgress size={16} /> : <FilterListIcon />}
              onClick={handleValidateCommissions}
              disabled={validating || loading}
              sx={{ ml: 1, position: 'relative' }}
            >
              Validate Commissions
              {validationIssues.length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: 'error.main',
                    color: 'white',
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    border: '2px solid white',
                  }}
                >
                  {validationIssues.length > 99 ? '99+' : validationIssues.length}
                </Box>
              )}
            </Button>
          </Box>
        </Box>
      </Box>
      
      {/* Summary Cards */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Total Commission</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                  <Typography 
                    color="primary" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      mr: 0.5
                    }}
                  >
                    {formatCurrencyDisplay(totalCommissionAmount).rmPart}
                  </Typography>
                  <Typography 
                    color="primary" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '2rem',
                      lineHeight: 1.2,
                      letterSpacing: '-0.5px'
                    }}
                  >
                    {formatCurrencyDisplay(totalCommissionAmount).amountPart}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2">
                {filteredCommissions.filter(comm => comm.status !== 'voided').length} services
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Pending Commission Finalize</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                  <Typography 
                    color="warning.main" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      mr: 0.5
                    }}
                  >
                    {formatCurrencyDisplay(totalPendingAmount).rmPart}
                  </Typography>
                  <Typography 
                    color="warning.main" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '2rem',
                      lineHeight: 1.2,
                      letterSpacing: '-0.5px'
                    }}
                  >
                    {formatCurrencyDisplay(totalPendingAmount).amountPart}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2">
                {filteredCommissions.filter(c => c.status === 'pending').length} services
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Paid Commission</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                  <Typography 
                    color="success.main" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      mr: 0.5
                    }}
                  >
                    {formatCurrencyDisplay(totalPaidAmount).rmPart}
                  </Typography>
                  <Typography 
                    color="success.main" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '2rem',
                      lineHeight: 1.2,
                      letterSpacing: '-0.5px'
                    }}
                  >
                    {formatCurrencyDisplay(totalPaidAmount).amountPart}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2">
                {filteredCommissions.filter(c => c.status === 'paid').length} services
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Staff Count</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                {staffSummaries.length}
              </Typography>
              <Typography variant="body2">
                with commissions this month
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
      
      {/* Staff Breakdown */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Staff Breakdown</Typography>
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                <TableCell>Staff Name</TableCell>
                <TableCell align="right">Services</TableCell>
                <TableCell align="right">Pending</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Voided</TableCell>
                <TableCell align="right">Total Commission</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {staffSummaries.map((staff) => (
                <TableRow key={staff.staffId} hover>
                  <TableCell>{staff.staffName}</TableCell>
                  <TableCell align="right">{staff.serviceCount}</TableCell>
                  <TableCell align="right">{formatCurrency(staff.pendingAmount)}</TableCell>
                  <TableCell align="right">{formatCurrency(staff.paidAmount)}</TableCell>
                  <TableCell align="right">{formatCurrency(staff.voidedAmount)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatCurrency(staff.totalCommission)}
                  </TableCell>
                </TableRow>
              ))}
              {staffSummaries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">No commission data available</TableCell>
                </TableRow>
              )}
            </TableBody>
            {staffSummaries.length > 0 && (
              <TableHead>
                <TableRow sx={{ 
                  '& th': { fontWeight: 600 },
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }}>
                  <TableCell>Total</TableCell>
                  <TableCell align="right">
                    {staffSummaries.reduce((sum, staff) => sum + staff.serviceCount, 0)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(staffSummaries.reduce((sum, staff) => sum + staff.pendingAmount, 0))}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(staffSummaries.reduce((sum, staff) => sum + staff.paidAmount, 0))}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(staffSummaries.reduce((sum, staff) => sum + staff.voidedAmount, 0))}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(staffSummaries.reduce((sum, staff) => sum + staff.totalCommission, 0))}
                  </TableCell>
                </TableRow>
              </TableHead>
            )}
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 2 
        }}>
          <Typography variant="h6">Commission Details</Typography>
          
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: 1,
            width: { xs: '100%', sm: 'auto' }
          }}>
            <TextField
              placeholder="Search commissions..."
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ flexGrow: { xs: 1, sm: 0 }, minWidth: { xs: '100%', sm: 170 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: { xs: '48%', sm: 120 } }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="voided">Voided</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: { xs: '48%', sm: 150 } }}>
              <InputLabel id="staff-filter-label">Staff</InputLabel>
              <Select
                labelId="staff-filter-label"
                id="staff-filter"
                value={selectedStaff}
                label="Staff"
                onChange={handleStaffFilterChange}
              >
                <MenuItem value="all">All Staff</MenuItem>
                {staffSummaries.map(staff => (
                  <MenuItem key={staff.staffId} value={staff.staffId}>
                    {staff.staffName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Sale #</TableCell>
                    <TableCell>Staff</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Service/Product</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Commission</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCommissions
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((commission) => (
                      <TableRow key={commission.id} hover>
                        <TableCell>{formatDate(commission.date)}</TableCell>
                        <TableCell>{commission.saleNumber}</TableCell>
                        <TableCell>{commission.staffName}</TableCell>
                        <TableCell>
                          {commission.id && commissionCategories[commission.id] ? 
                            commissionCategories[commission.id] : 
                            'Loading...'}
                        </TableCell>
                        <TableCell>{commission.serviceName}</TableCell>
                        <TableCell>{commission.customerName || 'Walk-in'}</TableCell>
                        <TableCell align="right">{formatCurrency(commission.servicePrice)}</TableCell>
                        <TableCell align="right">{(commission.commissionRate * 100).toFixed(0)}%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(commission.amount)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={statusDisplayMap[commission.status]?.label || commission.status}
                            color={statusDisplayMap[commission.status]?.color || 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {commission.paymentDate ? formatDate(commission.paymentDate) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredCommissions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center">No commission data available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredCommissions.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
      
      {/* Commission Validation Dialog */}
      <Dialog
        open={validationDialogOpen}
        onClose={handleCloseValidationDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Commission Validation Results
          <IconButton
            aria-label="close"
            onClick={handleCloseValidationDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {validationIssues.length === 0 ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              All commissions are valid. No issues found.
            </Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                Found {validationIssues.length} issues with commissions.
              </Alert>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Issue Type</TableCell>
                      <TableCell>Sale #</TableCell>
                      <TableCell>Sale Total</TableCell>
                      <TableCell>Commission</TableCell>
                      <TableCell>Rate</TableCell>
                      <TableCell>Staff</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationIssues.map((issue, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {issue.type === 'high-rate' ? 'High Commission Rate' : 'Missing Commission'}
                        </TableCell>
                        <TableCell>{issue.saleNumber}</TableCell>
                        <TableCell>{formatCurrency(issue.saleTotal)}</TableCell>
                        <TableCell>{formatCurrency(issue.commissionTotal)}</TableCell>
                        <TableCell>{issue.commissionRate.toFixed(2)}%</TableCell>
                        <TableCell>{issue.staffNames.join(', ')}</TableCell>
                        <TableCell>{formatDate(issue.date)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseValidationDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Commission GL Entry Preview Dialog */}
      <CommissionGLEntryPreviewDialog
        open={commissionGlEntryPreviewOpen}
        onClose={() => setCommissionGlEntryPreviewOpen(false)}
        onConfirm={handleConfirmCommissionGLEntries}
        entries={commissionGlEntryPreviews}
        month={selectedMonth}
        year={selectedYear}
        isSubmitting={isSubmittingCommissionGlEntries}
      />
      
      {/* Reverse GL Entries Confirmation Dialog */}
      <Dialog
        open={reverseDialogOpen}
        onClose={() => setReverseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Reverse GL Entries
          <IconButton
            aria-label="close"
            onClick={() => setReverseDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action will reverse the GL entries for {months[selectedMonth]} {selectedYear}.
            </Alert>
            
            {glTransactionToReverse ? (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Transaction Details
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Description:</strong> {glTransactionToReverse.description}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Date:</strong> {formatDate(glTransactionToReverse.transactionDate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Transaction ID:</strong> {glTransactionToReverse.id}
                  </Typography>
                </Box>
                
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  GL Entries to Reverse
                </Typography>
                
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell>Account</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Debit</TableCell>
                        <TableCell align="right">Credit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {glTransactionToReverse.entries.map((entry, index) => (
                        <TableRow key={index}>
                          <TableCell>{entry.accountNumber} - {entry.accountName}</TableCell>
                          <TableCell>{glTransactionToReverse.description}</TableCell>
                          <TableCell align="right">{entry.debit > 0 ? formatCurrency(entry.debit) : ''}</TableCell>
                          <TableCell align="right">{entry.credit > 0 ? formatCurrency(entry.credit) : ''}</TableCell>
                        </TableRow>
                      ))}
                      
                      {/* Totals row */}
                      <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Totals</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(glTransactionToReverse.entries.reduce((sum, entry) => sum + entry.debit, 0))}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(glTransactionToReverse.entries.reduce((sum, entry) => sum + entry.credit, 0))}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Are you sure you want to reverse these GL entries? This action cannot be undone.
                </Typography>
              </>
            ) : loadingTransactionDetails ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Typography variant="body1">
                Could not load transaction details. Please try again.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setReverseDialogOpen(false)} 
            color="primary"
            disabled={isReversingGLEntries}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReverseGLEntries} 
            color="error"
            variant="contained"
            disabled={isReversingGLEntries || !glTransactionToReverse}
            startIcon={isReversingGLEntries ? <CircularProgress size={16} /> : <UndoIcon />}
          >
            {isReversingGLEntries ? 'Reversing...' : 'Reverse GL Entries'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommissionBreakdown;
